import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  makeStyles,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { history } from 'helpers/history';
import { useParams } from 'react-router-dom';
import { primaryColor } from 'theme/theme';
import { OrganizationDepartmentResponse } from '.generated/api';
import clsx from 'clsx';

const useStyles = makeStyles(() => ({
  tableContainer: {
    width: '100%',
  },
  tableContainerWide: {
    marginTop: '1rem',
    marginLeft: '-1.5rem',
    width: 'calc(100% + 3rem)',
  },
  table: {
    color: primaryColor(),
  },
  tableHead: {
    backgroundColor: 'rgba(240, 241, 243, 1)',
    color: primaryColor(),
  },
  tableHeadCell: {
    fontWeight: 'bold',
    color: primaryColor(),
    padding: '0.7rem 1rem 0.5rem 1rem',
  },
  tableCell: {
    color: primaryColor(),
  },
  tableRow: {
    textDecoration: 'none',
    cursor: 'pointer',
  },
  textBold: {
    fontWeight: 'bold',
    color: primaryColor(),
  },
}));

interface ParamTypes {
  orgId: string;
}

type TableProps = {
  departments: OrganizationDepartmentResponse[] | undefined;
  handleDepartmentClick?: (departmentId: string) => void;
  inTab: boolean;
};

const DepartmentsTable: React.FC<TableProps> = (props) => {
  const { departments, inTab, handleDepartmentClick } = props;
  const classes = useStyles();
  const { t } = useTranslation(['translations']);
  const { orgId } = useParams<ParamTypes>();

  const handleClick = (departmentId: string) => {
    if (handleDepartmentClick) {
      handleDepartmentClick(departmentId);
    } else {
      history.push(`/organization/${orgId}/department/${departmentId}`);
    }
  };

  return (
    <TableContainer
      className={clsx(
        inTab ? classes.tableContainerWide : classes.tableContainer,
      )}
    >
      <Table aria-label="organization table" className={classes.table}>
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell className={classes.tableHeadCell}>
              {t('organization_page.departments.table.name')}
            </TableCell>
            <TableCell className={classes.tableHeadCell}>
              {t('organization_page.departments.table.admin')}
            </TableCell>
            <TableCell className={classes.tableHeadCell} align="right">
              {t('organization_page.departments.table.users')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {departments?.map((dept: OrganizationDepartmentResponse) => (
            <TableRow
              key={dept.name}
              hover={true}
              onClick={() => handleClick(dept.departmentId)}
              className={classes.tableRow}
            >
              <TableCell className={classes.textBold}>{dept.name}</TableCell>
              <TableCell className={classes.tableCell}>
                {dept.administrator}
              </TableCell>
              <TableCell className={classes.tableCell} align="right">
                {dept.numberOfUsers}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export { DepartmentsTable };
